import type { LayerAssetPresence } from '@civilgrid/utils/src/esri/layerAssetPresenceTypes';
import Dinero from 'dinero.js';
import { capitalize, mapValues } from 'lodash';
import { DateTime } from 'luxon';
import type { GetCostOutputBasic } from '~/common/billingUtils';
import type { ExcelSheetDef } from '~/common/exportUtils';
import type {
  Activity,
  Agency,
  CivilSearchMessage,
  CommentGroupObject,
  CommentObject,
  DataRequestV2,
  FileDownload,
  GeoItem,
  GeoItemEdits,
  IntelligenceSummary,
  MapDetail,
  MapObject,
  Map as MapType,
  Project,
  ProjectStoredFileType,
  UtilityNote,
} from '~/common/types';
import type { LngLat } from '~/components2/Map/canvas/SurfaceBase';
import type { Contractor } from '~/pages/projectDetails/pdfGeneration/permittingFormTypes';
import type { UtilityAgency, UtilityContact } from '~/pages/projectDetails/utilitiesV2/common';
import type { PropertyZoningDetails } from '../pages/projectDetails/landRights/GetZoningInfo/zoneTypes';
import type { ParcelDetails } from '../pages/projectDetails/landRights/store';
import { getAxiosClient, sortByName } from './common';

const axios = getAxiosClient();

export type TRequestType = 'dataRequested' | 'fiberDetailRequested' | 'asbuiltRequested';

function transformObject(o: any): MapObject {
  return {
    ...o,
    createdAt: DateTime.fromISO(o.createdAt),
    updatedAt: DateTime.fromISO(o.updatedAt || o.createdAt),
  };
}

function transformMap(map: any): MapType {
  return {
    ...map,
    objects: undefined,
    createdAt: DateTime.fromISO(map.createdAt),
    updatedAt: DateTime.fromISO(map.updatedAt || map.createdAt),
  };
}

function transformMapDetail(map: any): MapDetail {
  return {
    ...transformMap(map),
    objects: mapValues(map.objects, transformObject),
  };
}

export function transformProject<T = Project>(project: any): T {
  return {
    ...project,
    billingTier: project.billingTier || 'project',
    ...(project.startDate && {
      startDate: DateTime.fromISO(project.startDate),
    }),
    ...(project.endDate && { endDate: DateTime.fromISO(project.endDate) }),
    ...(project.constructionStartDate && {
      constructionStartDate: DateTime.fromISO(project.constructionStartDate),
    }),
    ...(project.constructionEndDate && {
      constructionEndDate: DateTime.fromISO(project.constructionEndDate),
    }),
    createdAt: DateTime.fromISO(project.createdAt),
    createdAtYear: DateTime.fromISO(project.createdAt).year,
    updatedAt: DateTime.fromISO(project.updatedAt),
    ...(project.utilityNotes && {
      utilityNotes: project.utilityNotes.map(transformUtilityNote),
    }),
  };
}

function transformAgency(agency: any): Agency | UtilityAgency {
  return {
    ...agency,
    sourceDate: agency.sourceDate ? DateTime.fromISO(agency.sourceDate) : undefined,
    ...(agency.lastRefresh && {
      lastRefresh: DateTime.fromISO(agency.lastRefresh),
    }),
    contacts: agency.contacts?.map(transformAgencyContact),
    createdAt: DateTime.fromISO(agency.createdAt),
    updatedAt: DateTime.fromISO(agency.updatedAt),
  };
}

function transformAgencyContact(contact: any): UtilityContact {
  return {
    ...contact,
    name: contact.firstName ? `${contact.firstName} ${contact.lastName}` : null,
  };
}

function transformDataRequest(request: any): DataRequestV2 {
  return {
    ...request,
    agency: request.agency ? transformAgency(request.agency) : undefined,
    activities: request.activities ? request.activities.map(transformActivity) : [],
    firstContactedAt: request.firstContactedAt ? DateTime.fromISO(request.firstContactedAt) : undefined,
    secondContactedAt: request.secondContactedAt ? DateTime.fromISO(request.secondContactedAt) : undefined,
    thirdContactedAt: request.thirdContactedAt ? DateTime.fromISO(request.thirdContactedAt) : undefined,
    fourthContactedAt: request.fourthContactedAt ? DateTime.fromISO(request.fourthContactedAt) : undefined,
    createdAt: DateTime.fromISO(request.createdAt),
    updatedAt: DateTime.fromISO(request.updatedAt),
  };
}

function transformActivity(activity: any): Activity {
  return {
    ...activity,
    createdAt: DateTime.fromISO(activity.createdAt),
    updatedAt: DateTime.fromISO(activity.updatedAt),
  };
}

function transformUtilityNote(note: any): UtilityNote {
  return {
    ...note,
    createdAt: DateTime.fromISO(note.createdAt),
    updatedAt: DateTime.fromISO(note.updatedAt),
  };
}

export function transformSummary(summary: any): IntelligenceSummary {
  const moratoriaInfo: IntelligenceSummary['moratoriaInfo'] = {};
  const pavingProjectsInfo: IntelligenceSummary['pavingProjectsInfo'] = {};
  const cipProjectsInfo: NonNullable<IntelligenceSummary['cipProjectsInfo']> = {};

  Object.keys(summary.moratoriaInfo).forEach((ownerId) => {
    const info = summary.moratoriaInfo[ownerId];
    moratoriaInfo[ownerId] = {
      ...info,
      ...(info?.startDate && {
        startDate: DateTime.fromISO(info.startDate, { zone: 'utc' }),
      }),
      ...(info?.endDate && {
        endDate: DateTime.fromISO(info.endDate, { zone: 'utc' }),
      }),
    };
  });

  Object.keys(summary.pavingProjectsInfo).forEach((ownerId) => {
    const info = summary.pavingProjectsInfo[ownerId];
    pavingProjectsInfo[ownerId] = {
      ...info,
      ...(info?.startDate && {
        startDate: DateTime.fromISO(info.startDate, { zone: 'utc' }),
      }),
      ...(info?.endDate && {
        endDate: DateTime.fromISO(info.endDate, { zone: 'utc' }),
      }),
    };
  });

  Object.keys(summary.cipProjectsInfo ?? {}).forEach((ownerId) => {
    const info = summary.cipProjectsInfo[ownerId];
    cipProjectsInfo[ownerId] = {
      ...info,
      ...(info?.startDate && {
        startDate: DateTime.fromISO(info.startDate, { zone: 'utc' }),
      }),
      ...(info?.endDate && {
        endDate: DateTime.fromISO(info.endDate, { zone: 'utc' }),
      }),
    };
  });

  return {
    ...summary,
    moratoriaInfo,
    pavingProjectsInfo,
    cipProjectsInfo,
    schedule: {
      ...summary.schedule,
      ...(summary.schedule.conflictsStart && {
        conflictsStart: DateTime.fromISO(summary.schedule.conflictsStart).toUTC(),
      }),
      ...(summary.schedule.conflictsEnd && {
        conflictsEnd: DateTime.fromISO(summary.schedule.conflictsEnd).toUTC(),
      }),
    },
    project: transformProject(summary.project),
  };
}

export async function newProject(project: any, organizationId: string) {
  const { data } = await axios({
    method: 'POST',
    url: `/v2/projects/?organizationId=${organizationId}`,
    data: project,
  });

  return {
    ...data,
    createdAt: DateTime.fromISO(data.createdAt),
    updatedAt: DateTime.fromISO(data.updatedAt),
  };
}

export async function getProjects(organizationId: string, compact = false, toggleAllProjects: boolean) {
  const { data } = await axios({
    method: 'GET',
    url: `/v2/projects?organizationId=${organizationId}&compact=${compact}&toggleAllProjects=${toggleAllProjects}`,
  });

  const formatted = data.map(transformProject);

  return sortByName(formatted);
}

export async function listIntelligenceSummaries(organizationId: string): Promise<IntelligenceSummary[]> {
  const { data } = await axios.get(`/v2/projects/intelligenceSummary?organizationId=${organizationId}`);

  return data.map(transformSummary);
}

export async function listSummariesById(
  organizationId: string,
  summaryIds: string[],
  controller: AbortController,
): Promise<IntelligenceSummary[]> {
  const { data } = await axios.get(
    `/v2/projects/intelligenceSummary?organizationId=${organizationId}&summaryIds=${JSON.stringify(summaryIds)}`,
    {
      signal: controller.signal,
    },
  );

  return data.map(transformSummary);
}

export async function exportIntelligenceSummariesToExcel(
  organizationId: string,
  sheetDefs: ExcelSheetDef[],
): Promise<Buffer> {
  const { data } = await axios({
    method: 'POST',
    url: `/v2/projects/intelligenceSummary/export/excel?organizationId=${organizationId}`,
    data: { sheetDefs },
    responseType: 'arraybuffer',
  });

  return data;
}

export async function updateIntelligenceSummary(
  summaryId: string,
  properties: Partial<IntelligenceSummary>,
): Promise<IntelligenceSummary> {
  const { data } = await axios({
    method: 'PUT',
    url: `/v2/projects/intelligenceSummary/${summaryId}`,
    data: { properties },
  });

  return data;
}

export async function getProject<T = Project>(projectId: string, orgId?: string): Promise<T> {
  const { data } = await axios({
    method: 'GET',
    url: `/v2/projects/${projectId}?timestamp=${Date.now()}`,
    params: {
      orgId,
    },
  });

  return transformProject<T>(data);
}

export async function updateBillingTier(projectId: string, tier: Project['billingTier'], costInfo: GetCostOutputBasic) {
  const { data } = await axios({
    method: 'POST',
    url: `/v2/projects/${projectId}/updateBillingTier`,
    data: {
      tier,
      costInfo,
    },
  });
  return transformProject(data);
}

export async function archiveProject(projectId: string) {
  const { data } = await axios({
    method: 'DELETE',
    url: `/v2/projects/${projectId}`,
  });
  return data;
}

export async function getMaps(projectId: string) {
  const { data } = await axios({
    method: 'GET',
    url: `/v2/projects/${projectId}/maps`,
  });
  return data.map(transformMap);
}

export async function newMap(projectId: string, name: string | null) {
  const { data } = await axios({
    method: 'POST',
    url: `/v2/projects/${projectId}/maps`,
    data: { name },
  });
  return transformMapDetail(data);
}

export async function getMap(projectId: string, mapId: string) {
  const { data } = await axios({
    method: 'GET',
    url: `/v2/projects/${projectId}/maps/${mapId}`,
  });
  return transformMapDetail(data);
}

export async function duplicateMap(projectId: string, mapId: string) {
  const { data } = await axios({
    method: 'GET',
    url: `/v2/projects/${projectId}/maps/duplicate?mapId=${mapId}`,
  });
  return transformMapDetail(data);
}

export async function updateMap(projectId: string, map: MapType) {
  const { data } = await axios({
    method: 'PUT',
    url: `/v2/projects/${projectId}/maps/${map.id}`,
    data: map,
  });
  return transformMap(data);
}

export async function deleteMap(projectId: string, mapId: string) {
  const { data } = await axios({
    method: 'DELETE',
    url: `/v2/projects/${projectId}/maps/${mapId}`,
  });
  return data;
}

export async function newObject(projectId: string, map: MapType, object: Omit<MapObject, 'createdBy' | 'updatedBy'>) {
  const { data } = await axios({
    method: 'POST',
    url: `/v2/projects/${projectId}/maps/${map.id}/object`,
    data: object,
  });
  return transformObject(data);
}

export async function newObjects(
  projectId: string,
  map: MapType,
  objects: Omit<MapObject[], 'createdBy' | 'updatedBy'>,
) {
  const { data } = await axios({
    method: 'POST',
    url: `/v2/projects/${projectId}/maps/${map.id}/objects`,
    data: objects,
  });

  const objs = data.map((obj: any) => transformObject(obj));

  return objs;
}

export async function updateObject(projectId: string, map: MapType, object: MapObject) {
  const { data } = await axios({
    method: 'PUT',
    url: `/v2/projects/${projectId}/maps/${map.id}/objects/${object.id}`,
    data: object,
  });
  return transformObject(data);
}

export async function deleteObject(projectId: string, map: MapType, object: MapObject) {
  const { data } = await axios({
    method: 'DELETE',
    url: `/v2/projects/${projectId}/maps/${map.id}/objects/${object.id}`,
  });
  return transformObject(data);
}

/** Permitting PDF embed uses this id client-side only — must not hit the API. */
const EPHEMERAL_PERMITTING_MAP_ID = '__permitting_local_map__';

export const addObjectFile = async ({
  projectId,
  mapId,
  objectId,
  file,
}: {
  projectId: string;
  mapId: string;
  objectId: string;
  file: FormData;
}) => {
  if (mapId === EPHEMERAL_PERMITTING_MAP_ID) {
    return Promise.reject(new Error('Object file upload is disabled for the permitting map (local-only session).'));
  }
  const { data } = await axios({
    method: 'POST',
    url: `/v2/projects/${projectId}/maps/${mapId}/objects/${objectId}/files`,
    data: file,
  });
  return data;
};

export const deleteObjectFiles = async ({
  projectId,
  mapId,
  objectId,
  files,
}: {
  projectId: string;
  mapId: string;
  objectId: string;
  files: string[];
}) => {
  if (mapId === EPHEMERAL_PERMITTING_MAP_ID) {
    return Promise.reject(new Error('Object file delete is disabled for the permitting map (local-only session).'));
  }
  const { data } = await axios({
    method: 'DELETE',
    url: `/v2/projects/${projectId}/maps/${mapId}/objects/${objectId}/files`,
    data: files,
  });
  return data;
};

export async function undoDelete({
  projectId,
  mapId,
  objectId,
}: {
  projectId: string;
  mapId: string;
  objectId: string;
}) {
  const { data } = await axios({
    method: 'PATCH',
    url: `/v2/projects/${projectId}/maps/${mapId}/objects/${objectId}`,
  });
  return transformObject(data);
}

// ---- Permit PDF drafts ----------------------------------------------------------------------------
// A permit draft persists in the permitPdf table with a FK to a maps row (mapType = 'permit') that holds
// the drawn objects/files. Dates in formData are ISO strings; the form converts to/from DatePicker values.

export type PermitMapView = { center: [number, number]; zoom: number; bearing?: number; pitch?: number };

export type PermitFormDataPayload = {
  permitSubmissionDate: string | null;
  permitSubmissionContactName: string;
  permitSubmissionContactEmail: string;
  permitSubmissionContactPhone: string;
  workStartDate: string | null;
  workEndDate: string | null;
  projectNumber: string;
  address: string;
  city: string;
  zip: string;
  atmosRep: string;
  atmosEmail: string;
  atmosPhone: string;
  workTypeText: string;
  workTypeRadio: 'main' | 'service' | 'emergency';
  mapOnSeparatePage: boolean;
};

export type UpsertPermitPayload = {
  formData: PermitFormDataPayload;
  contractors: Contractor[];
  mapView: PermitMapView | null;
  objects: MapObject[];
};

export type PermitDraft = {
  id: string;
  projectId: string;
  mapId: string;
  formData: PermitFormDataPayload;
  contractors: Contractor[];
  mapView: PermitMapView | null;
  status: string;
  /** Hydrated permit map (objects keyed by id), or null if the map could not be loaded. */
  map: MapDetail | null;
};

function transformPermitDraft(data: any): PermitDraft {
  return { ...data, map: data.map ? transformMapDetail(data.map) : null };
}

export async function getPermit(projectId: string): Promise<PermitDraft | null> {
  const { data } = await axios({
    method: 'GET',
    url: `/v2/projects/${projectId}/permit`,
  });
  return data ? transformPermitDraft(data) : null;
}

export async function upsertPermit(projectId: string, payload: UpsertPermitPayload): Promise<PermitDraft> {
  const { data } = await axios({
    method: 'PUT',
    url: `/v2/projects/${projectId}/permit`,
    data: payload,
  });
  return transformPermitDraft(data);
}

export async function deletePermit(projectId: string): Promise<void> {
  await axios({
    method: 'DELETE',
    url: `/v2/projects/${projectId}/permit`,
  });
}

// Type casting below to resolve, "Argument of type 'DateTime' is not assignable to parameter of type 'string'."
export async function addComment(
  projectId: string,
  mapId: string,
  objectId: string,
  comment: { [key: string]: string },
): Promise<CommentGroupObject> {
  const { data, status } = await axios({
    method: 'POST',
    url: `/v2/projects/${projectId}/maps/${mapId}/objects/${objectId}/comment`,
    data: comment,
  });
  if (status !== 200) {
    throw new Error('Failed to add comment');
  }

  return {
    ...data,
    createdAt: DateTime.fromISO(data.createdAt as unknown as string),
    updatedAt: DateTime.fromISO(data.updatedAt as unknown as string),
    commentGroup: data.commentGroup.map((comment: CommentObject) => ({
      ...comment,
      createdAt: DateTime.fromISO(comment.createdAt as unknown as string),
      updatedAt: DateTime.fromISO(comment.updatedAt as unknown as string),
    })),
  };
}

// Type casting below to resolve, "Argument of type 'DateTime' is not assignable to parameter of type 'string'."
export async function updateComment(
  projectId: string,
  mapId: string,
  objectId: string,
  commentId: string,
  comment: { comment: string },
): Promise<CommentGroupObject> {
  const { data, status } = await axios({
    method: 'PATCH',
    url: `/v2/projects/${projectId}/maps/${mapId}/objects/${objectId}/comment/${commentId}`,
    data: comment,
  });
  if (status !== 200) {
    throw new Error('Failed to update comment');
  }

  return {
    ...data,
    createdAt: DateTime.fromISO(data.createdAt as unknown as string),
    updatedAt: DateTime.fromISO(data.updatedAt as unknown as string),
    commentGroup: data.commentGroup.map((comment: CommentObject) => ({
      ...comment,
      createdAt: DateTime.fromISO(comment.createdAt as unknown as string),
      updatedAt: DateTime.fromISO(comment.updatedAt as unknown as string),
    })),
  };
}

// Delete comment
// Type casting below to resolve, "Argument of type 'DateTime' is not assignable to parameter of type 'string'."
export async function archiveComment(
  projectId: string,
  mapId: string,
  objectId: string,
  commentId: string,
): Promise<CommentGroupObject> {
  const { data, status } = await axios({
    method: 'DELETE',
    url: `/v2/projects/${projectId}/maps/${mapId}/objects/${objectId}/comment/${commentId}`,
  });
  if (status !== 200) {
    throw new Error('Failed to archive comment');
  }
  data.commentGroup = data.commentGroup.map((comment: CommentObject) => ({
    ...comment,
    createdAt: DateTime.fromISO(comment.createdAt as unknown as string),
    updatedAt: DateTime.fromISO(comment.updatedAt as unknown as string),
  }));
  return {
    ...data,
    createdAt: DateTime.fromISO(data.createdAt),
    updatedAt: DateTime.fromISO(data.updatedAt),
  };
}

export async function parcelPointQuery(projectId: string, lng: number, lat: number) {
  const { data } = await axios({
    method: 'GET',
    url: `/v1/projects/${projectId}/parcelPointQuery?lng=${lng}&lat=${lat}`,
  });
  return data;
}

export async function getParcelData({ lng, lat }: LngLat) {
  const { data, status } = await axios({
    method: 'GET',
    url: `/v2/projects/getParcelData/${lng}/${lat}`,
  });

  if (status !== 200) {
    throw new Error('Failed to get parcel data.');
  }

  return { data, status };
}

export async function fetchGeotech(
  projectId: string,
  boringLogIds: string[],
  type: string,
  wellCompletionReportData: any = null,
) {
  const { data } = await axios({
    method: 'POST',
    url: `/v1/projects/${projectId}/fetchGeotech`,
    data: { boringLogIds, type, wellCompletionReportData },
  });
  return data;
}

export async function calculateWatershedRisk(projectId: string) {
  const { data } = await axios({
    method: 'GET',
    url: `/v1/misc/calculateWatershedRisk?projectId=${projectId}`,
  });
  return data;
}

export async function printProjectPDF(
  projectId: string,
  type: string,
  parcelDetails: ParcelDetails[],
  parcelRequests: any[] = [],
  ccEmails?: string[],
): Promise<unknown> {
  const { data } = await axios({
    method: 'POST',
    url: `/v1/projects/${projectId}/print`,
    data: { parcelDetails, parcelRequests, type, ccEmails },
  });
  return data;
}

export async function getZoningData(
  projectId: string,
  apn: string,
  centroid: number[],
): Promise<PropertyZoningDetails> {
  const { data } = await axios({
    method: 'GET',
    url: `/v1/projects/${projectId}/getZoningData?apn=${apn}&lat=${centroid[1]}&lng=${centroid[0]}`,
  });
  return data;
}

export async function listGeoItems(projectId: string): Promise<GeoItem[]> {
  const { data } = await axios({
    method: 'GET',
    url: `/v1/projects/${projectId}/geoItems`,
  });
  return data;
}

export function transformGeoItem(item: any): GeoItem {
  return {
    ...item,
    createdAt: DateTime.fromISO(item.createdAt),
    updatedAt: DateTime.fromISO(item.updatedAt),
    fields:
      item.props?.reduce(
        (acc: any, curr: any) => ({
          ...acc,
          [curr.label]: curr,
        }),
        {},
      ) || {},
  };
}

export async function listGeoItemsV2(projectId: string): Promise<GeoItem[]> {
  const { data } = await axios.get(`/v1/projects/${projectId}/geoItems`);
  return data.map(transformGeoItem);
}

export async function editGeoItem(projectId: string, geoItemId: string, geoItemEdits: GeoItemEdits) {
  const { data } = await axios({
    method: 'PUT',
    url: `/v1/projects/${projectId}/geoItems/${geoItemId}`,
    data: geoItemEdits,
  });
  return data;
}

export type CreateGeoItemFileInput = {
  path: string;
  name?: string;
  type?: string;
  notes?: string;
  thumbnailPath?: string;
  compassHeading?: number | null;
};

export type CreateGeoItemInput = {
  type: string;
  geometry: { type: 'Point'; coordinates: number[] };
  props?: Array<{ label: string; type: string; value: string | number }>;
  files?: CreateGeoItemFileInput[];
};

export async function createGeoItem(
  projectId: string,
  input: CreateGeoItemInput,
  signal?: AbortSignal,
): Promise<GeoItem> {
  const { data } = await axios({
    method: 'POST',
    url: `/v1/projects/${projectId}/geoItems`,
    data: input,
    timeout: 30_000,
    signal,
  });
  return data;
}

export type UploadedGeoItemPhoto = { name: string; path: string; type: string; notes: string; signedPath: string };

// Uploads one photo to S3 without associating it to projectFiles; the returned path is attached to a geoItem instead.
export async function uploadGeoItemPhoto(
  projectId: string,
  file: File,
  signal?: AbortSignal,
): Promise<UploadedGeoItemPhoto> {
  const form = new FormData();
  form.append('file', file);
  form.append('name', file.name);
  form.append('type', file.type);
  form.append('extension', file.name.split('.').pop() ?? '');

  const { data } = await axios({
    method: 'POST',
    url: `/v1/projects/${projectId}/files?skipAddToProject=true`,
    data: form,
    timeout: 120_000,
    signal,
  });
  return data;
}

export async function printGeoItems(
  projectId: string,
  geoItemIds: string[],
  reportType: string,
  exportType?: 'pdf' | 'zip',
  ccEmails?: string[],
) {
  const { data } = await axios({
    method: 'POST',
    url: `/v1/projects/${projectId}/geoItems/${reportType}/print`,
    data: { geoItemIds, exportType, ccEmails },
  });

  return data;
}

export async function saveParcelDetails(projectId: string, parcelDetails: any[]) {
  const { data } = await axios({
    method: 'POST',
    url: `/v1/projects/${projectId}/parcelDetails`,
    data: parcelDetails,
  });
  return data;
}

export async function addMembers(projectId: string, members: string[]) {
  const { data } = await axios({
    method: 'POST',
    url: `/v2/projects/${projectId}/members`,
    data: {
      members: members,
    },
  });
  return data;
}

export async function removeMember(projectId: string, memberId: string) {
  const { data } = await axios({
    method: 'DELETE',
    url: `/v2/projects/${projectId}/members/${memberId}`,
  });
  return data;
}

export async function joinProject(projectId: string) {
  const { data } = await axios({
    method: 'POST',
    url: `/v2/projects/${projectId}/join`,
  });
  return data;
}

export async function leaveProject(projectId: string) {
  const { data } = await axios({
    method: 'DELETE',
    url: `/v2/projects/${projectId}/leave`,
  });
  return data;
}

export async function addFile(projectId: string, file: any) {
  const { data } = await axios({
    method: 'POST',
    url: `/v1/projects/${projectId}/files`,
    data: file,
  });

  return data;
}

export async function deleteFile(projectId: string, fileId: string) {
  const { data } = await axios({
    method: 'DELETE',
    url: `/v1/projects/${projectId}/files/${fileId}`,
  });

  return data;
}

export async function updateFile(projectId: string, fileId: string, updates: { [key: string]: string }) {
  const { data } = await axios({
    method: 'PUT',
    url: `/v1/projects/${projectId}/files/${fileId}`,
    data: updates,
  });

  return data;
}

export async function printGeoItem(
  projectId: string,
  geoItemIds: string[],
  reportType: string,
  reportInfo?: { [key: string]: string },
  ccEmails?: string[],
) {
  const { data } = await axios({
    method: 'POST',
    url: `/v1/projects/${projectId}/geoItems/${reportType}/print`,
    data: { geoItemIds, reportInfo, ccEmails },
  });
  return data;
}

export async function getLayers(projectId: string) {
  const { data, status } = await axios({
    method: 'GET',
    url: `/v2/projects/${projectId}/getLayers`,
  });

  if (status !== 200) {
    throw new Error('Error getting project features');
  }

  return data;
}

export async function getUtilityAgencies<T = any>(
  projectId: string,
  geometry?: GeoJSON.Geometry,
  v2?: boolean,
): Promise<T> {
  const { data } = await axios({
    method: 'POST',
    url: `/v2/projects/${projectId}/getUtilityAgencies`,
    data: { ...(geometry && { geometry }), ...(v2 !== undefined && { v2 }) },
    timeout: 60_000, // this request can take a while to complete for large geometries
  });

  return {
    agencies: data.agencies.map(transformAgency),
    dataRequests: data.dataRequests.map(transformDataRequest),
  } as T;
}

export async function populateProjectAgencies(projectId: string) {
  const response = await axios({
    method: 'POST',
    url: `/v2/projects/${projectId}/agencies`,
  });
  return response.data;
}

export async function getProjectAgencies(projectId: string): Promise<Array<UtilityAgency>> {
  const { data } = await axios({
    method: 'GET',
    url: `/v2/projects/${projectId}/agencies`,
    // Long term this can be removed as the data will be prepopulated, but for
    // now, not all projects will have gone through the job creation task and
    // may need to populate on-load here.
    timeout: 60_000, // this request can take a while to complete for large geometries
  });

  return data.map(transformAgency);
}

export async function getDataRequestsV2(projectId: string): Promise<DataRequestV2[]> {
  const { data } = await axios.get(`/v2/projects/${projectId}/getDataRequestsV2`);
  return data.map(transformDataRequest);
}

export async function submitDataRequestsV2(
  projectId: string,
  requests: Partial<DataRequestV2>[],
): Promise<DataRequestV2[]> {
  const { data } = await axios({
    url: `/v2/projects/${projectId}/submitDataRequestsV2`,
    method: 'POST',
    data: { requests },
  });

  return data;
}

export async function getCivilSearchMessages(projectId: string): Promise<CivilSearchMessage[]> {
  const { data } = await axios.get(`/v2/projects/${projectId}/civilSearchMessages`);
  return data;
}

export async function newCivilSearchMessage(
  projectId: string,
  message: { [key: string]: any },
): Promise<CivilSearchMessage> {
  const { data } = await axios({
    url: `/v2/projects/${projectId}/civilSearchMessages`,
    method: 'POST',
    data: { message },
  });

  return data;
}

export async function getLatestCivilSearchActivity(projectId: string): Promise<{
  message?: CivilSearchMessage;
  activity?: Activity;
}> {
  const { data } = await axios.get(`/v2/projects/${projectId}/latestCivilSearchActivity`);
  return data;
}

export async function getProjectFeatures(projectId: string) {
  const { data, status } = await axios({
    method: 'GET',
    url: `/v1/projects/${projectId}/getProjectFeatures`,
  });

  if (status !== 200) {
    throw new Error('Error getting project features');
  }

  return data;
}

export async function requestData(
  projectId: string,
  requestedIdentifiers: { [key: string]: any }[],
  type: TRequestType,
) {
  const { data } = await axios({
    method: 'POST',
    url: `/v1/projects/${projectId}/requestData`,
    data: { requestedIdentifiers, type },
  });

  return data;
}

export async function getStatePlaneZone(projectId: string) {
  const { data } = await axios({
    method: 'GET',
    url: `/v1/projects/${projectId}/getStatePlaneZone`,
  });

  return data;
}

export async function deleteGeoItem(projectId: string, geoItemId: string, baseVersion?: number) {
  const { data } = await axios({
    method: 'DELETE',
    url: `/v1/projects/${projectId}/geoItems/${geoItemId}`,
    params: baseVersion === undefined ? undefined : { baseVersion },
  });
  return data;
}

export async function editProject(projectId: string, edits: { attributes: { [key: string]: any } }) {
  const { data } = await axios({
    method: 'PUT',
    url: `/v2/projects/${projectId}`,
    data: edits.attributes,
  });

  return transformProject(data);
}

export async function soilsRequest(projectId: string, featureIds: string[]) {
  const { data } = await axios({
    method: 'POST',
    url: `/v1/projects/${projectId}/soilsRequest`,
    data: { featureIds },
  });

  return data;
}

export async function checkForSoilsRequest(projectId: string) {
  const { data } = await axios({
    method: 'GET',
    url: `/v1/projects/${projectId}/checkForSoilsRequest`,
  });

  return data;
}

export async function getTransactions<T = any>(projectId: string): Promise<T> {
  const { data } = await axios({
    method: 'GET',
    url: `/v2/projects/${projectId}/transactions`,
  });

  return data.map((t: any) => ({
    ...t,
    amount: Dinero({ amount: t.amount }),
    createdAt: DateTime.fromISO(t.createdAt),
    updatedAt: DateTime.fromISO(t.updatedAt),
  }));
}

export async function printReceipt(
  projectId: string,
  type: 'docx' | 'pdf',
  dateRange?: { startDate: string; endDate: string } | undefined,
  ccEmails?: string[],
) {
  const { data } = await axios({
    method: 'POST',
    url: `/v2/projects/${projectId}/printReceipt`,
    data: { type, dateRange, ccEmails },
  });

  return data;
}

export async function printQuote(projectId: string, type: 'docx' | 'pdf', ccEmails?: string[]) {
  const { data } = await axios({
    method: 'POST',
    url: `/v2/projects/${projectId}/printQuote`,
    data: { type, ccEmails },
  });

  return data;
}

interface LinearGeometry {
  buffer: {
    offset: number;
    units: 'feet';
  };
  geometry: GeoJSON.MultiLineString;
}

export async function editProjectGeometry(
  projectId: string,
  amount: number,
  geometry: GeoJSON.MultiPolygon,
  linear?: LinearGeometry,
  shouldAddChangeOrderFee?: boolean,
  circle?: Project['circle'],
): Promise<{ success: boolean; notifyUser: boolean; transactionId: string }> {
  const { data } = await axios({
    method: 'PUT',
    url: `/v2/projects/${projectId}/editProjectGeometry`,
    data: { geometry, linear, amount, shouldAddChangeOrderFee, circle },
  });

  return data;
}

export interface TIntelligenceFeatures {
  nearbyProjects: {
    plannedProjects: GeoJSON.FeatureCollection;
    pavingProjects: GeoJSON.FeatureCollection;
    pavingMoratoria: GeoJSON.FeatureCollection;
  };
  bounds: GeoJSON.Feature;
}

export async function getIntelligenceFeatures<T = unknown>(projectId: string): Promise<T> {
  const { data } = await axios.get(`/v2/projects/${projectId}/getIntelligenceFeatures`);

  return data;
}

export async function updateIntelligenceStatus(projectId: string, featureId: string, reviewStatus: string) {
  const { data } = await axios({
    method: 'PUT',
    url: `/v2/projects/${projectId}/updateIntelligenceStatus`,
    data: { featureId, reviewStatus },
  });

  return data;
}

export async function addIntelligenceComment<T = any>(
  projectId: string,
  featureId: string,
  comment: string,
): Promise<T> {
  const { data } = await axios({
    method: 'PUT',
    url: `/v2/projects/${projectId}/addIntelligenceComment`,
    data: { featureId, comment },
  });

  return data;
}

export async function deleteIntelligenceComment<T = any>(
  projectId: string,
  featureId: string,
  commentId: string,
): Promise<T> {
  const { data } = await axios({
    method: 'DELETE',
    url: `/v2/projects/${projectId}/deleteIntelligenceComment`,
    data: { featureId, commentId },
  });

  return data;
}

export async function generateProposalPacket(projectId: string, grid: any, ccEmails?: string[]) {
  const { data } = await axios({
    method: 'POST',
    url: `/v2/projects/${projectId}/generateProposalPacket`,
    data: {
      grid,
      ccEmails,
    },
  });
  return data;
}

export async function generateRecordsResearchReport(projectId: string, ccEmails?: string[]) {
  const { data } = await axios({
    method: 'POST',
    url: `/v2/projects/${projectId}/generateRecordsResearchReport`,
    data: { ccEmails },
  });

  return data;
}

export async function getProjectDownloads(projectId: string) {
  const { data } = await axios({
    method: 'GET',
    url: `/v2/projects/${projectId}/download`,
  });

  return data;
}

export interface DownloadSignedUrlResponse {
  url: string;
  filename: string;
}

/**
 * A short-lived signed URL for one export object, minted per click rather than per list row so the
 * browser is handed a link the server has just confirmed.
 */
export async function getDownloadSignedUrl(
  projectId: string,
  downloadId: string,
  target: 'file' | 'combined',
): Promise<DownloadSignedUrlResponse> {
  const { data } = await axios({
    method: 'GET',
    url: `/v2/projects/${projectId}/downloads/${downloadId}/signedUrl`,
    params: { target },
  });

  return data;
}

/** Table a Utilities-tab file row lives in; mirrors the API's `utilitiesFileSources`. */
export type UtilitiesFileSource = 'dataRequest' | 'supportingFile' | 'message' | 'projectFile';

export interface UtilitiesFileSignedUrlResponse {
  url: string;
  filename: string;
  /** S3 object size in bytes. */
  filesize: number;
}

/** A short-lived attachment URL for one Utilities-tab file, minted per click. */
export async function getUtilitiesFileSignedUrl(
  projectId: string,
  source: UtilitiesFileSource,
  fileId: string,
): Promise<UtilitiesFileSignedUrlResponse> {
  const { data } = await axios({
    method: 'GET',
    url: `/v2/projects/${projectId}/utilities/files/${source}/${fileId}/signedUrl`,
  });

  return data;
}

/** A short-lived signed attachment URL for one of the project's stored files, minted per click. */
export async function getStoredFileDownloadUrl(
  projectId: string,
  fileType: ProjectStoredFileType,
  fileId: string,
): Promise<FileDownload> {
  const { data } = await axios({
    method: 'GET',
    url: `/v2/projects/${projectId}/files/${fileType}/${fileId}/downloadUrl`,
  });

  return data;
}

export async function submitDownloadRequest({
  projectId,
  projectionId,
  formats,
  layers,
  extractTemplateId,
  ccEmails,
}: {
  projectId: string;
  projectionId: string;
  formats: string[];
  layers: string[];
  extractTemplateId?: string | null;
  ccEmails?: string[];
}) {
  const { data } = await axios({
    method: 'POST',
    url: `/v2/projects/${projectId}/submitDownloadRequest`,
    data: { projectionId, formats, layers, extractTemplateId, ccEmails },
  });

  return data;
}

type RequestOptions = { signal?: AbortSignal; timeout?: number };

export async function getDownloadRestrictions<T = any>(projectId: string, options?: RequestOptions): Promise<T> {
  const { data } = await axios({
    method: 'GET',
    url: `/v2/projects/${projectId}/getDownloadRestrictions`,
    signal: options?.signal,
    timeout: options?.timeout,
  });

  return data;
}

export async function getDigitizationAccess(
  projectId: string,
  options?: RequestOptions,
): Promise<{ hasDigitizationAccess: boolean }> {
  const { data } = await axios({
    method: 'GET',
    url: `/v2/projects/${projectId}/getDigitizationAccess`,
    signal: options?.signal,
    timeout: options?.timeout,
  });
  return data;
}

/**
 * Report whether each layer has features inside the project boundary. Layers that could not be
 * verified come back as `unknown` and must stay selectable. Deliberately uncached so an `unknown`
 * is retried on the next load.
 */
export async function getLayerAssetPresence(
  projectId: string,
  layerIds: string[],
): Promise<{ presence: Record<string, LayerAssetPresence> }> {
  const { data } = await axios.post(`/v2/projects/${projectId}/layerAssetPresence`, { layerIds });
  return data;
}

export async function generateFloodReport(projectId: string, ccEmails?: string[]) {
  const { data } = await axios({
    method: 'POST',
    url: `/v2/projects/${projectId}/generateFloodReport`,
    data: { ccEmails },
  });
  return data;
}

export async function getCivilSearchActivities(projectId: string): Promise<Activity[]> {
  const { data } = await axios.get(`/v2/projects/${projectId}/getCivilSearchActivities`);
  return data.map(transformActivity);
}

export async function getHistoricalUSGSMaps(projectId: string) {
  const { data } = await axios({
    method: 'GET',
    url: `/v2/projects/${projectId}/getHistoricalUSGSMaps`,
  });
  return data;
}

export async function getCGSRegulatoryQuadrangleList(projectId: string) {
  const { data } = await axios({
    method: 'GET',
    url: `/v2/projects/${projectId}/getCGSRegulatoryQuadrangleList`,
  });
  return data;
}

export async function generateEvidenceEmailChain(
  projectId: string,
  ccEmails?: string[],
): Promise<{
  success: boolean;
  requestIds: string[];
}> {
  const { data } = await axios.post(`/v2/projects/${projectId}/generateEvidenceEmailChain`, { ccEmails });

  return data;
}

export async function downloadCSAttachments(projectId: string, ccEmails?: string[]) {
  const { data } = await axios({
    method: 'POST',
    url: `/v2/projects/${projectId}/downloadCSAttachments`,
    data: { ccEmails },
  });

  return data;
}

export async function updateUtilityNote(projectId: string, utilityNoteId: string, notes: string): Promise<UtilityNote> {
  const { data } = await axios({
    method: 'PUT',
    url: `/v2/projects/${projectId}/utilityNotes/${utilityNoteId}`,
    data: { notes },
  });

  return transformUtilityNote(data);
}

export async function getFullProjectZoning(projectId: string) {
  const { data } = await axios({
    method: 'GET',
    url: `/v2/projects/${projectId}/getFullProjectZoning`,
  });

  return data;
}

export async function getAntennaData(projectId: string) {
  const { data } = await axios({
    method: 'GET',
    url: `/v2/projects/${projectId}/antennaMap`,
  });

  return data;
}

export async function getEnvironmentalStudyData(projectId: string) {
  const { data } = await axios({
    method: 'GET',
    url: `/v2/projects/${projectId}/getEnvironmentalStudyData`,
  });

  return data;
}

export async function getEnvironmentalStudyPDF(projectId: string) {
  const url = `/v2/projects/${projectId}/getEnvironmentalStudyPDF`;

  const { data } = await axios({
    method: 'GET',
    url,
  });

  return data;
}

export async function requestErisData(projectId: string) {
  const { data } = await axios({
    method: 'POST',
    url: `/v2/projects/${projectId}/requestErisData`,
  });

  return data;
}

export async function getProjectSummary<T>(projectId: string, type: string) {
  const { data } = await axios({
    method: 'GET',
    url: `/v2/projects/${projectId}/get${capitalize(type)}Summary`,
  });

  return data as T;
}
